<template>
    <div class="p-3">
        <form @submit.prevent="handleSubmit" autocomplete="off">
            <!-- Validation error -->
            <div v-if="errorMessage" class="alert alert-danger text-white text-sm py-2 px-3 mb-3">
                <i class="fas fa-exclamation-circle me-2"></i>
                {{ errorMessage }}
            </div>

            <!-- Token invalidation warning (edit only) -->
            <div v-if="isEdit && credentialsTouched"
                class="alert alert-warning text-dark text-sm py-2 px-3 mb-3">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Esto invalida el token cacheado del partner (el sistema lo regenera automáticamente).
            </div>

            <div class="row g-3">
                <!-- Partner name -->
                <div class="col-md-6 col-12">
                    <label class="form-label fw-semibold text-sm" for="ec-partner">
                        Nombre del aliado <span class="text-danger">*</span>
                    </label>
                    <input id="ec-partner" type="text" class="form-control" v-model.trim="form.partner_name"
                        placeholder="Ej. Aliado Habi" :disabled="saving" />
                </div>

                <!-- Admin destino -->
                <div class="col-md-6 col-12">
                    <label class="form-label fw-semibold text-sm" for="ec-admin">
                        Admin destino <span class="text-danger">*</span>
                    </label>
                    <v-select inputId="ec-admin" v-model="form.assigned_user_id" :options="adminOptions"
                        :reduce="o => o.value" label="label" :clearable="false" :disabled="saving"
                        :filterable="false" :loading="loadingAdmins" @search="onSearch"
                        placeholder="Busca por correo…" class="ec-select">
                        <template #no-options="{ search }">
                            <span class="text-sm text-secondary">
                                {{ search ? 'Sin resultados' : 'Escribe para buscar…' }}
                            </span>
                        </template>
                    </v-select>
                </div>

                <!-- Client ID -->
                <div class="col-md-6 col-12">
                    <label class="form-label fw-semibold text-sm" for="ec-client-id">
                        Client ID
                        <span v-if="!isEdit" class="text-danger">*</span>
                    </label>
                    <input id="ec-client-id" type="text" class="form-control" v-model.trim="form.client_id"
                        placeholder="OAuth Conecta client_id" autocomplete="off" :disabled="saving" />
                </div>

                <!-- Client secret -->
                <div class="col-md-6 col-12">
                    <label class="form-label fw-semibold text-sm" for="ec-client-secret">
                        <i class="fas fa-lock text-info me-1"></i>
                        Client secret
                        <span v-if="!isEdit" class="text-danger">*</span>
                    </label>
                    <input id="ec-client-secret" type="password" class="form-control" v-model="form.client_secret"
                        :placeholder="isEdit ? 'Dejar vacío para no cambiar' : 'OAuth Conecta client_secret'"
                        autocomplete="new-password" :disabled="saving" />
                </div>

                <!-- API Key -->
                <div class="col-md-6 col-12">
                    <label class="form-label fw-semibold text-sm" for="ec-api-key">
                        <i class="fas fa-lock text-info me-1"></i>
                        API Key
                        <span v-if="!isEdit" class="text-danger">*</span>
                    </label>
                    <input id="ec-api-key" type="password" class="form-control" v-model="form.api_key"
                        :placeholder="isEdit ? 'Dejar vacío para no cambiar' : 'Header x-api-key'"
                        autocomplete="new-password" :disabled="saving" />
                </div>

                <!-- Conecta scope -->
                <div class="col-md-6 col-12">
                    <label class="form-label fw-semibold text-sm" for="ec-scope">
                        Conecta scope <span class="text-muted">(opcional)</span>
                    </label>
                    <input id="ec-scope" type="text" class="form-control" v-model.trim="form.conecta_scope"
                        :placeholder="defaultScope" :disabled="saving" />
                </div>

                <!-- Frecuencia sync -->
                <div class="col-md-6 col-12">
                    <label class="form-label fw-semibold text-sm" for="ec-freq">
                        Frecuencia sync (horas)
                    </label>
                    <input id="ec-freq" type="number" min="1" max="168" class="form-control"
                        v-model.number="form.sync_frequency_hours" :disabled="saving" />
                    <small class="text-muted">Entre 1 y 168 horas.</small>
                </div>

                <!-- Activo -->
                <div class="col-md-6 col-12 d-flex align-items-center">
                    <div class="form-check form-switch mt-3">
                        <input id="ec-active" class="form-check-input" type="checkbox" role="switch"
                            v-model="form.active" :disabled="saving" />
                        <label class="form-check-label fw-semibold text-sm" for="ec-active">Activo</label>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="d-flex gap-3 justify-content-end mt-4 pt-3 border-top">
                <soft-button type="button" color="secondary" variant="outline" size="sm"
                    @click="$emit('cancel')" :disabled="saving">
                    <i class="fas fa-times me-1"></i> Cancelar
                </soft-button>
                <soft-button type="submit" color="success" variant="gradient" size="sm" :loading="saving">
                    <i v-if="!saving" class="fas fa-save me-1"></i>
                    Guardar
                </soft-button>
            </div>
        </form>
    </div>
</template>

<script lang="js">
import { ref, computed, watch } from 'vue';
import SoftButton from '@/components/SoftButton.vue';
import { DEFAULT_CONECTA_SCOPE } from '../useExternalCredentials.js';

export default {
    name: 'CredentialFormModal',
    components: { SoftButton },
    props: {
        credential: { type: Object, default: null },
        admins: { type: Array, default: () => [] },
        saving: { type: Boolean, default: false },
        loadingAdmins: { type: Boolean, default: false },
    },
    emits: ['save', 'cancel', 'search'],
    setup(props, { emit }) {
        const defaultScope = DEFAULT_CONECTA_SCOPE;
        const errorMessage = ref('');

        const buildForm = () => ({
            partner_name: props.credential?.partner_name || '',
            client_id: props.credential?.client_id || '',
            client_secret: '',
            api_key: '',
            conecta_scope: props.credential?.conecta_scope || '',
            assigned_user_id: props.credential?.assigned_user_id ?? null,
            active: props.credential ? !!props.credential.active : true,
            sync_frequency_hours: props.credential?.sync_frequency_hours ?? 6,
        });

        const form = ref(buildForm());

        watch(() => props.credential, () => {
            form.value = buildForm();
            errorMessage.value = '';
        });

        const isEdit = computed(() => !!props.credential?.id);

        const currentAssignedEmail = computed(
            () => props.credential?.assignedUser?.email || ''
        );

        // Opciones para v-select (búsqueda por correo). Incluye el admin actual
        // aunque no esté en la lista cargada, para no perder el valor en edición.
        const adminOptions = computed(() => {
            const opts = props.admins.map(a => ({ label: a.email, value: a.id }));
            const assignedId = props.credential?.assigned_user_id;
            if (assignedId != null && !opts.some(o => o.value === assignedId)) {
                opts.unshift({ label: currentAssignedEmail.value || `Usuario #${assignedId}`, value: assignedId });
            }
            return opts;
        });

        // En edición: avisar si se tocan client_id / client_secret / api_key / scope
        const credentialsTouched = computed(() => {
            if (!isEdit.value) return false;
            const c = props.credential || {};
            return (
                form.value.client_id !== (c.client_id || '') ||
                (form.value.client_secret || '') !== '' ||
                (form.value.api_key || '') !== '' ||
                form.value.conecta_scope !== (c.conecta_scope || '')
            );
        });

        const buildPayload = () => {
            if (!isEdit.value) {
                // Alta: enviar todo lo requerido
                const payload = {
                    partner_name: form.value.partner_name,
                    client_id: form.value.client_id,
                    client_secret: form.value.client_secret,
                    api_key: form.value.api_key,
                    assigned_user_id: form.value.assigned_user_id,
                    active: form.value.active,
                    sync_frequency_hours: form.value.sync_frequency_hours,
                };
                if (form.value.conecta_scope) payload.conecta_scope = form.value.conecta_scope;
                return payload;
            }

            // Edición: solo enviar lo que cambió
            const c = props.credential || {};
            const payload = {};
            if (form.value.partner_name !== (c.partner_name || '')) payload.partner_name = form.value.partner_name;
            if (form.value.client_id !== (c.client_id || '')) payload.client_id = form.value.client_id;
            if (form.value.client_secret) payload.client_secret = form.value.client_secret;
            if (form.value.api_key) payload.api_key = form.value.api_key;
            if (form.value.conecta_scope !== (c.conecta_scope || '')) payload.conecta_scope = form.value.conecta_scope;
            if (form.value.assigned_user_id !== c.assigned_user_id) payload.assigned_user_id = form.value.assigned_user_id;
            if (form.value.active !== !!c.active) payload.active = form.value.active;
            if (form.value.sync_frequency_hours !== c.sync_frequency_hours) {
                payload.sync_frequency_hours = form.value.sync_frequency_hours;
            }
            return payload;
        };

        const validate = () => {
            if (!form.value.partner_name) return "El nombre del aliado es requerido.";
            if (!form.value.assigned_user_id) return "Debes seleccionar un admin destino.";
            if (!isEdit.value) {
                if (!form.value.client_id) return "El Client ID es requerido.";
                if (!form.value.client_secret) return "El Client secret es requerido.";
                if (!form.value.api_key) return "El API Key es requerido.";
            }
            const f = form.value.sync_frequency_hours;
            if (f != null && f !== '' && (f < 1 || f > 168)) {
                return "La frecuencia de sync debe estar entre 1 y 168 horas.";
            }
            return '';
        };

        // Búsqueda server-side de admins con debounce (cubre todas las páginas)
        let searchTimer = null;
        const onSearch = (query) => {
            if (searchTimer) clearTimeout(searchTimer);
            searchTimer = setTimeout(() => emit('search', query), 350);
        };

        const handleSubmit = () => {
            const err = validate();
            if (err) {
                errorMessage.value = err;
                return;
            }
            errorMessage.value = '';
            const payload = buildPayload();
            if (isEdit.value && Object.keys(payload).length === 0) {
                errorMessage.value = "No hay cambios para guardar.";
                return;
            }
            emit('save', payload);
        };

        return {
            defaultScope,
            errorMessage,
            form,
            isEdit,
            adminOptions,
            credentialsTouched,
            onSearch,
            handleSubmit,
        };
    }
};
</script>

<style lang="scss" scoped>
.form-control:focus,
.form-select:focus {
    border-color: #ef9a9a;
    box-shadow: 0 0 0 2px rgba(229, 57, 53, 0.12);
}

// vue-select alineado con los inputs Bootstrap
.ec-select {
    :deep(.vs__dropdown-toggle) {
        border: 1px solid #d2d6da;
        border-radius: 0.5rem;
        padding: 0.18rem 0.25rem;
        background: #fff;
    }

    &.vs--open :deep(.vs__dropdown-toggle),
    :deep(.vs__dropdown-toggle:focus-within) {
        border-color: #ef9a9a;
        box-shadow: 0 0 0 2px rgba(229, 57, 53, 0.12);
    }

    :deep(.vs__search),
    :deep(.vs__selected) {
        font-size: 0.875rem;
        color: #495057;
    }

    :deep(.vs__dropdown-menu) {
        font-size: 0.875rem;
    }
}
</style>
